import { DetailTranslationKeys, ErrorTranslationKeys } from '../../../api/models/TranslationKeys';
import { QuoteApi } from '../../../api/price';
import { timeStringToMs } from '../../utility/DateFormatter';
import { Log, Registry, Router } from '@lightningjs/sdk';
import { SelectRentPage } from '../../../pages/detail/rent/SelectRentPage';
import { AssetType, StreamType } from '../../../api/models/Shared.types';
import { CodeInputDialog, CodeInputDialogHelper } from '../../../components/shared/CodeInputDialog';
import { formatQualityStringForPriceAndResolution } from '../../utility/Formatters';
import { BundleAsset } from '../bundle/BundleAsset';
import { IRentableQuality, QuoteType } from '../detail.types';
import { DetailAction } from './DetailAction';
import { StreamResolutionUtil } from '../../utility/StreamResolutionUtil';
import { VodStreamInfo } from '../../player/streamInfo/VodStreamInfo';
import { StartPositionType } from '../../player/streamInfo/StreamInfo';
import { Backend } from '../../../api/Backend';
import { VodAsset } from '../vod/VodAsset';
import { VodStreamRights } from '../vod/VodStreamRights';
import {
  ICommitQuote3,
  IQuotePriceResponse3,
  IResolutionPriceResponse3,
  IStreamStartResponseV2,
  IValidQuotePriceResponse3,
} from '../../../api/models/AndromeTV.types';
import { OptionData } from '../../../components/option/OptionData';

export class RentAction extends DetailAction {
  private type: AssetType;
  private rightsHandler: VodStreamRights | undefined;
  private asset: VodAsset | BundleAsset;
  private pendingQuote: IQuotePriceResponse3 | undefined;
  private closeOptionTimeout: number | undefined;
  private pendingQual: IRentableQuality | undefined;
  private qualities: IRentableQuality[] = [];
  private showDiscount: boolean | undefined;
  private reservedStreamResponse: IStreamStartResponseV2 | undefined;
  constructor(
    backend: Backend,
    type: AssetType,
    asset: VodAsset | BundleAsset,
    rightsHandler: VodStreamRights | undefined = undefined
  ) {
    super(backend);
    this.type = type;
    this.rightsHandler = rightsHandler;
    this.asset = asset;
  }

  override doAction() {
    this.openRentDialog();
  }

  openRentDialog() {
    //TODO check adult
    this._backend.cachedQuoteApi.clearCache();
    this._backend.cachedQuoteApi.list(this._backend, this._backend.deviceProfile?.profile.catalog).then(quotes => {
      if (this.type === AssetType.VOD) {
        this.showVodOptions(quotes);
      } else if (this.type === AssetType.BUNDLE) {
        this.showBundleOptions(quotes);
      }
    });
  }

  showVodOptions(quotes: IValidQuotePriceResponse3[]) {
    if (!this.rightsHandler) return;

    this.rightsHandler.calculateStreamRightsForNewQuotes(quotes);
    if (!this.rightsHandler.ottStreamsLeftToRent()) {
      //on Android: try and start to watch else nothing
      //on Web: error_no_rentoptions
      this.owner?.fireAncestors(
        '$showMessage',
        this._backend.translations.forKey(ErrorTranslationKeys.error_no_rentoptions)
      );
      this.hideOptions(true);
      return;
    }

    if (this.asset instanceof VodAsset) {
      QuoteApi.createForVod(this._backend, this.asset.assetId, this._backend.sessionInfo?.device.id)
        .then(quote => {
          this.pendingQuote = quote;
          this.onQuote(quote);
        })
        .catch(err => {
          this.owner?.fireAncestors(
            '$showMessage',
            this._backend.translations.forKey(ErrorTranslationKeys.error_general)
          );
          Log.info(err);
        });
    }
  }

  hideOptions(shouldDeleteReservedStream: boolean) {
    this.owner?.fireAncestors('$closeDialog');
    this.releaseRent(shouldDeleteReservedStream);
  }

  releaseRent(shouldDeleteReservedStream: boolean) {
    if (this.closeOptionTimeout) {
      clearTimeout(this.closeOptionTimeout);
      this.closeOptionTimeout = undefined;
    }
    if (this.pendingQuote) {
      QuoteApi.delete(this._backend, this.pendingQuote.quoteId).then(() => {
        this.pendingQuote = undefined;
        this.pendingQual = undefined;
      });
    }

    if (shouldDeleteReservedStream) {
      this._backend.playerController.streamManager?.deleteReservedStream();
    }
  }

  onQuote(quote: IQuotePriceResponse3 | undefined, showDiscountOverride: boolean | undefined = undefined) {
    if (!quote) {
      this.owner?.fireAncestors(
        '$showMessage',
        this._backend.translations.forKey(ErrorTranslationKeys.error_no_rentoptions)
      );
      this.hideOptions(true);
      return;
    }

    const closeTime = Math.max(timeStringToMs(quote.endTime) - Date.now(), 0);

    if (this.closeOptionTimeout) Registry.clearTimeout(this.closeOptionTimeout);
    this.closeOptionTimeout = Registry.setTimeout(() => this.hideOptions(true), closeTime);
    this.qualities = [];

    if (this.type === AssetType.VOD) {
      this.qualities = this.getVodRentableQualities(quote);
    } else if (this.type === AssetType.BUNDLE) {
      this.qualities = this.getBundleRentableQualities(quote);
    }

    this.owner?.fireAncestors('$openDialog', {
      type: SelectRentPage,
      params: {
        backend: this._backend,
        name: 'Select rent option page',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        title: this.asset.title || '',
        options: this.qualities.map(q => ({ text: q.text, value: q })),
        onSuccess: (val: OptionData) => this.qualitySelected(val.value, quote),
        onCancelled: this.hideOptions.bind(this),
        onCoupon: this.openCouponDialog.bind(this),
        showDiscount: showDiscountOverride === undefined ? this.showDiscount : showDiscountOverride,
      },
    });
  }

  qualitySelected(quality: IRentableQuality, quote: IQuotePriceResponse3) {
    const selectedQual = quality;
    this.pendingQual = selectedQual;
    if (!selectedQual) {
      this.owner?.fireAncestors('$showMessage', this._backend.translations.forKey(ErrorTranslationKeys.error_general));
      return;
    }

    if (quality.quote.type === QuoteType.TVOD) {
      this._backend.playerController.streamManager
        ?.reserveStream(StreamType.TVOD, selectedQual.quote.assetId, quote.quoteId)
        .then(result => {
          this.reservedStreamResponse = result;
          this.openPinCodeDialog();
        })
        .catch(err => {
          Log.error('[RentAction]', err);
          // E.g. not allowed to stream this video by backend
          this.owner?.fireAncestors(
            '$showMessage',
            this._backend.translations.forKey(ErrorTranslationKeys.error_general)
          );
        });
    } else {
      this.openPinCodeDialog();
    }
  }

  openPinCodeDialog() {
    const inputmodal: any = CodeInputDialogHelper.getConfirmRentCodeInput(
      this._backend,
      (caller: CodeInputDialog, code: string) => this.tryPincode(caller, code),
      () => this.hideOptions(true)
    );
    this.owner?.fireAncestors('$openDialog', inputmodal);
  }

  openCouponDialog() {
    const inputmodal: any = CodeInputDialogHelper.getCouponCodeInput(
      this._backend,
      (caller: CodeInputDialog, code: string) => this.applyCoupon(caller, code),
      () => this.onQuote(this.pendingQuote) //re-open the list of qualities
    );
    this.owner?.fireAncestors('$openDialog', inputmodal);
  }

  getVodRentableQualities(quote: IQuotePriceResponse3): IRentableQuality[] {
    if (!this.rightsHandler) return [];

    const rentableQualities: IRentableQuality[] = [];
    this.showDiscount = false;

    this.rightsHandler.rentableOttStreams
      .sort((a, b) => {
        return (
          (StreamResolutionUtil.toInt(b.stream.resolution) || 0) -
          (StreamResolutionUtil.toInt(a.stream.resolution) || 0)
        );
      })
      .forEach(ottStream => {
        const quoteUpdate = quote.assets?.find(asset => asset.assetId === ottStream.stream.streamId);

        if (quoteUpdate) {
          this.showDiscount =
            this.showDiscount ||
            (quoteUpdate.price &&
              quoteUpdate.price.customerPrice !== undefined &&
              quoteUpdate.price.customerPrice !== 0);

          rentableQualities.push({
            text: formatQualityStringForPriceAndResolution(
              this._backend,
              quoteUpdate.price,
              ottStream.stream.resolution.toUpperCase(),
              quoteUpdate.validity
            ),
            quote: quoteUpdate,
          });
        }
      });

    return rentableQualities;
  }

  tryPincode(eventCaller: CodeInputDialog, code: string) {
    if (!this.pendingQual?.quote.assetId) return;
    const commitQuote: ICommitQuote3 = {
      assetId: this.pendingQual?.quote.assetId,
      pinRequest: {
        pinCode: code,
        type: 'VOD',
      },
    };

    if (this.pendingQuote) {
      QuoteApi.commit(this._backend, this.pendingQuote.quoteId, commitQuote)
        .then(result => {
          this._backend.cachedQuoteApi.addQuoteToCache(result);

          this.owner?.fireAncestors('$reloadDetailContent');
          //close the input modal
          this.owner?.fireAncestors('$closeDialog');

          if (this.type === AssetType.VOD && this.asset instanceof VodAsset) {
            this.rightsHandler?.calculateStreamRightsForNewQuotes([result]);
            const ottStream = this.rightsHandler?.getWatchableOttStream();
            if (ottStream) {
              const streamInfo = new VodStreamInfo(this.asset, ottStream, undefined, StartPositionType.START);
              Router.navigate('play', { streamInfo });
            } else {
              this.owner?.fireAncestors(
                '$showMessage',
                this._backend.translations.forKey(ErrorTranslationKeys.error_streaming)
              );
            }
          } else if (this.type === AssetType.BUNDLE) {
            this.owner?.fireAncestors(
              '$showMessage',
              this._backend.translations.forKey(DetailTranslationKeys.detail_succes_rent_bundle)
            );
          }

          this.pendingQuote = undefined;
          this.pendingQual = undefined;
        })
        .catch(err => {
          Log.error('[RentAction]', err);
          eventCaller.errorMsg = this._backend.translations.forKey(ErrorTranslationKeys.AA_INVALID_PINCODE);
        });
    }
  }

  applyCoupon(eventCaller: CodeInputDialog, code: string) {
    code = code.toUpperCase();
    if (!this.pendingQuote) return;
    QuoteApi.update(this._backend, this.pendingQuote.quoteId, code)
      .then(quote => {
        this.pendingQuote = quote;
        this.onQuote(quote, false);
      })
      .catch(err => {
        Log.error('[RentAction]', err);
        eventCaller.errorMsg = this._backend.translations.forKey(ErrorTranslationKeys.PRICING_COUPON_NOT_FOUND);
      });
  }

  showBundleOptions(quotes: IValidQuotePriceResponse3[]) {
    if (!(this.asset instanceof BundleAsset)) {
      Log.error('[VodRentAction]', 'asset is not right type');
      return;
    }
    this.asset.updateFromQuotes(quotes);

    if (!this.asset.streamsLeftToRent()) {
      this.owner?.fireAncestors(
        '$showMessage',
        this._backend.translations.forKey(ErrorTranslationKeys.error_no_rentoptions)
      );
      this.hideOptions(true);
      return;
    }

    QuoteApi.createForBundle(this._backend, this.asset.id, this._backend.sessionInfo?.device.id)
      .then(price => {
        this.pendingQuote = price;
        this.onQuote(price);
      })
      .catch(err => {
        Log.error('[RentAction]', err);
        this.owner?.fireAncestors(
          '$showMessage',
          this._backend.translations.forKey(ErrorTranslationKeys.error_general)
        );
      });
  }

  getBundleRentableQualities(quote: IQuotePriceResponse3): IRentableQuality[] {
    const result: IRentableQuality[] = [];

    let showDiscount: boolean | undefined = false;
    if (!(this.asset instanceof BundleAsset)) return result;
    this.asset.rentableBundleResolutions
      ?.sort((a: IResolutionPriceResponse3, b: IResolutionPriceResponse3) => {
        return (StreamResolutionUtil.toInt(b.resolution) || 0) - (StreamResolutionUtil.toInt(a.resolution) || 0);
      })
      .forEach((resolution: IResolutionPriceResponse3) => {
        const quoteUpdate = quote.assets?.find(asset => asset.assetId === resolution.resolutionId);

        if (quoteUpdate) {
          showDiscount =
            showDiscount ||
            (quoteUpdate.price &&
              quoteUpdate.price.customerPrice !== undefined &&
              quoteUpdate.price.customerPrice !== 0);

          result.push({
            text: formatQualityStringForPriceAndResolution(
              this._backend,
              quoteUpdate.price,
              resolution.resolution.toUpperCase(),
              quoteUpdate.validity
            ),
            quote: quoteUpdate,
          });
        }
      });

    this.showDiscount = showDiscount;
    return result;
  }
}
